import { action, computed, makeObservable, observable } from 'mobx';
import { StoreNode } from '../../store';
import { Store } from '../../store/store';
import { JobModel } from '../../entities';
import { MomentResultItem, MomentResultsCatalog } from '../../pages/searchResultsPage/momentResultsCatalog';
import { JobResultsCatalog } from '../../pages/searchResultsPage/jobResultsCatalog';
import { Metadata } from '@clipr/lib';
import { ApiRequestOptions } from '../../api';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';

export class SearchService
  extends StoreNode {

  constructor(store: Store) {
    super(store);
    makeObservable(this, {
      teamId: observable,
      metadataFilter: computed,
      tagsFilter: computed,
      showSuggestions: computed,
      jobSuggestions: computed,
      topicSuggestions: computed,
      momentSuggestions: computed,
      hasSuggestions: computed,
      isLoadingSuggestions: computed,
      hasResults: computed,
      isLoading: computed,
      fetchSuggestions: action,
      clearSuggestions: action,
      handleChange: action,
      fetchVideos: action,
      fetchMoments: action,
      fetchTopics: action,
      search: action,
      setTeamId: action,
      submitToGptPage: action,
      submitToSearchPage: action,
      loadValueToSearchPage: action,
      resetSearchResults: action,
    });

    this.jobCatalog = new JobResultsCatalog(this.store, { metadataFilter: () => this.metadataFilter, tagsFilter: () => this.tagsFilter, teamId: () => this.teamId });
    this.momentCatalog = new MomentResultsCatalog(this.store, { type: 'Moment', metadataFilter: () => this.metadataFilter, tagsFilter: () => this.tagsFilter, teamId: () => this.teamId });
    this.topicCatalog = new MomentResultsCatalog(this.store, { type: 'Topic', metadataFilter: () => this.metadataFilter, tagsFilter: () => this.tagsFilter, teamId: () => this.teamId });
  }

  readonly jobCatalog: JobResultsCatalog;
  readonly momentCatalog: MomentResultsCatalog;
  readonly topicCatalog: MomentResultsCatalog;

  teamId: string | null = null;

  get metadataFilter(): Partial<Metadata> | null {
    return this.store.widgetService.metadataFilter;
  }

  get tagsFilter(): string[] | null {
    return this.store.widgetService.tagsFilter;
  }

  get showSuggestions(): boolean {
    return this.store.searchResultsPage.showSuggestions;
  }

  get jobSuggestions(): JobModel[] {
    return this.jobCatalog.suggestions;
  }

  get topicSuggestions(): MomentResultItem[] {
    return this.topicCatalog.suggestions;
  }

  get momentSuggestions(): MomentResultItem[] {
    return this.momentCatalog.suggestions;
  }

  get hasSuggestions(): boolean {
    return !isEmpty(this.jobCatalog.suggestions) ||
      !isEmpty(this.topicCatalog.suggestions) ||
      !isEmpty(this.momentCatalog.suggestions);
  }

  get isLoadingSuggestions(): boolean {
    return this.jobCatalog.isFetchingSuggestions ||
      this.topicCatalog.isFetchingSuggestions ||
      this.momentCatalog.isFetchingSuggestions;
  }

  get hasResults(): boolean {
    return !this.jobCatalog.isEmpty || !this.topicCatalog.isEmpty || !this.momentCatalog.isEmpty;
  }

  get isLoading(): boolean {
    return this.jobCatalog.isLoading || this.topicCatalog.isLoading || this.momentCatalog.isLoading;
  }

  async fetchSuggestions(value: string, opts?: ApiRequestOptions) {
    return await Promise.all([
      this.jobCatalog.fetchSuggestions(value, opts),
      this.topicCatalog.fetchSuggestions(value, opts),
      this.momentCatalog.fetchSuggestions(value, opts),
    ]);
  }

  debouncedFetch = debounce(this.fetchSuggestions, 500);

  clearSuggestions() {
    this.jobCatalog.suggestionsLookup.clear();
    this.topicCatalog.suggestionsLookup.clear();
    this.momentCatalog.suggestionsLookup.clear();
  }

  handleChange = async (e: { value: string }) => {
    const { value } = e;

    if (value && value.length >= 3) {
      this.debouncedFetch(value);
    } else {
      this.clearSuggestions();
    }
  }

  async fetchVideos(pageSize: number, value?: string | null, opts?: ApiRequestOptions) {
    return this.jobCatalog.fetchVideos(value, pageSize, opts);
  }

  async fetchMoments(pageSize: number, value?: string | null, opts?: ApiRequestOptions) {
    return this.momentCatalog.fetchMoments(value, pageSize, opts);
  }

  async fetchTopics(pageSize: number, value?: string | null, opts?: ApiRequestOptions) {
    return this.topicCatalog.fetchMoments(value, pageSize, opts);
  }

  async search(pageSize: number, value?: string | null, opts?: ApiRequestOptions) {
    return await Promise.all([
      this.fetchVideos(pageSize, value, opts),
      this.fetchTopics(pageSize, value, opts),
      this.fetchMoments(pageSize, value, opts)
    ]);
  }

  debouncedSearch = debounce(this.search, 500);

  fetchData(pageSize: number, value?: string) {
    if (!this.isLoading) {
      this.search(pageSize, value);
    }

    this.jobCatalog.fetchPopularVideos();
  }

  setTeamId(teamId: string | null) {
    this.teamId = teamId;
  }

  // TODO: remove direct dependency on pages and decouple from search pages

  submitToGptPage(replaceRoute = false, teamId: string | null, value?: string | null) {
    this.store.cliprGptSearchResultPageState.submit(replaceRoute, teamId, value);
  }

  submitToSearchPage(replaceOnSubmit: boolean = false, teamId: string | null) {
    this.store.searchResultsPage.submit(replaceOnSubmit, teamId);
  }

  loadValueToSearchPage(value?: string | null) {
    this.store.searchResultsPage.loadValue(value);
  }

  resetSearchResults() {
    this.store.searchResultsPage.reset();
  }

  getSearchModel() {
    return this.store.searchResultsPage.model;
  }
}
